<template>
  <div class="group-categories-modern">
    <!-- Modals -->
    <NewAssociation
      :associations="associations"
      :groups="stock_groups"
      :categories="stock_item_categories"
      @created="getAssociations"
    />

    <!-- Main Content -->
    <div class="categories-content">
      <!-- Header Section -->
      <div class="content-header">
        <div class="header-left">
          <h2 class="section-title">
            <i class="fas fa-link section-icon"></i>
            Associar Categorias
          </h2>
          <p class="section-subtitle">
            Vincule categorias aos grupos de estoque apropriados
          </p>
        </div>
        <div class="header-actions">
          <v-btn
            @click="$bvModal.show('new_association')"
            class="btn-add-modern"
          >
            <i class="fas fa-link mr-2"></i>
            Nova Associação
          </v-btn>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-card">
          <div class="filter-group">
            <label class="filter-label">
              <i class="fas fa-search filter-icon"></i>
              Pesquisar
            </label>
            <b-input
              v-model="search"
              placeholder="Digite o nome do grupo ou categoria"
              class="modern-input"
            />
          </div>
        </div>
      </div>

      <!-- Content Grid -->
      <div class="content-grid">
        <!-- Table Section -->
        <div class="table-section">
          <!-- No Results -->
          <div v-show="associations.length == 0" class="no-results">
            <i class="fas fa-link no-results-icon"></i>
            <h3 class="no-results-title">Nenhuma associação encontrada</h3>
            <p class="no-results-text">
              Tente ajustar os filtros ou crie uma nova associação
            </p>
          </div>

          <!-- Data Table -->
          <div v-show="associations.length > 0" class="table-card">
            <v-data-table
              dense
              :headers="headers"
              :items="associations"
              :search="search"
              :footer-props="{
                'items-per-page-options': [50, 75, 100],
                'items-per-page-text': 'Mostrar',
              }"
              class="modern-table"
              :no-data-text="'Nenhuma associação encontrada'"
              :loading-text="'Carregando associações...'"
            >
              <template #[`item.group_name`]="{ item }">
                <div class="group-name-cell">
                  <div class="group-name">
                    <i class="fas fa-warehouse mr-2 group-icon"></i>
                    {{ item.group_name }}
                  </div>
                </div>
              </template>

              <template #[`item.category_name`]="{ item }">
                <div class="category-name-cell">
                  <div class="category-name">
                    <i
                      :class="getCategoryIcon(item.stock_item_type_name)"
                      class="mr-2 category-icon"
                    ></i>
                    {{ item.category_name }}
                  </div>
                  <div class="category-type">
                    <span
                      class="type-badge"
                      :class="getTypeBadgeClass(item.stock_item_type_name)"
                    >
                      {{ item.stock_item_type_name }}
                    </span>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="actions-cell">
                  <confirmation-dialog
                    small
                    message-title="EXCLUIR ASSOCIAÇÃO"
                    message-body="Deseja realmente excluir esta associação? Esta ação não pode ser desfeita."
                    open-class="btn-action-delete"
                    open-left-icon="fa-unlink"
                    open-text="Desassociar"
                    @yes="
                      disassociate(
                        item.stock_group_id,
                        item.stock_item_category_id
                      )
                    "
                  />
                </div>
              </template>
            </v-data-table>
          </div>
        </div>

        <!-- Info Section -->
        <div class="info-section">
          <div class="info-card">
            <div class="info-header">
              <h3 class="info-title">
                <i class="fas fa-info-circle mr-2"></i>
                Sobre Associações
              </h3>
            </div>
            <div class="info-content">
              <div class="info-item">
                <h4 class="info-subtitle">O que são associações?</h4>
                <p class="info-text">
                  Informam ao sistema qual <strong>categoria</strong> é
                  armazenada em cada <strong>grupo</strong>, definindo a
                  organização do estoque.
                </p>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Regras importantes:</h4>
                <div class="rules-list">
                  <div class="rule-item">
                    <div class="rule-icon restriction">
                      <i class="fas fa-times"></i>
                    </div>
                    <div class="rule-content">
                      <strong>Restrição de tipos</strong>
                      <span
                        >Não é permitido associar <strong>produtos</strong> em
                        locais de <strong>medicamentos</strong> ou
                        vice-versa</span
                      >
                    </div>
                  </div>
                  <div class="rule-item">
                    <div class="rule-icon best-practice">
                      <i class="fas fa-lightbulb"></i>
                    </div>
                    <div class="rule-content">
                      <strong>Boa prática</strong>
                      <span
                        >Organize categorias similares no mesmo grupo para
                        facilitar a gestão</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Exemplos de associações:</h4>
                <div class="examples-list">
                  <div class="example-association">
                    <div class="association-group">
                      <i class="fas fa-pills example-icon"></i>
                      <span>Farmácia</span>
                    </div>
                    <i class="fas fa-arrow-right association-arrow"></i>
                    <div class="association-category">
                      <i class="fas fa-capsules example-icon"></i>
                      <span>Medicamentos</span>
                    </div>
                  </div>
                  <div class="example-association">
                    <div class="association-group">
                      <i class="fas fa-utensils example-icon"></i>
                      <span>Cozinha</span>
                    </div>
                    <i class="fas fa-arrow-right association-arrow"></i>
                    <div class="association-category">
                      <i class="fas fa-apple-alt example-icon"></i>
                      <span>Alimentos</span>
                    </div>
                  </div>
                  <div class="example-association">
                    <div class="association-group">
                      <i class="fas fa-warehouse example-icon"></i>
                      <span>Almoxarifado</span>
                    </div>
                    <i class="fas fa-arrow-right association-arrow"></i>
                    <div class="association-category">
                      <i class="fas fa-spray-can example-icon"></i>
                      <span>Produtos de Limpeza</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Como funciona:</h4>
                <div class="steps-list">
                  <div class="step-item">
                    <div class="step-number">1</div>
                    <div class="step-content">
                      <strong>Selecione um grupo</strong>
                      <span>Escolha o local de armazenamento</span>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">2</div>
                    <div class="step-content">
                      <strong>Escolha uma categoria</strong>
                      <span>Defina o tipo de item a ser armazenado</span>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">3</div>
                    <div class="step-content">
                      <strong>Confirme a associação</strong>
                      <span>A vinculação será criada automaticamente</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Messages -->
      <ErrorsMessages :errors="errors" v-if="errors.length > 0" />
    </div>
  </div>
</template>

<script>
import NewAssociation from "./new-association.vue";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import ErrorsMessages from "../../Components/ErrorsMessages.vue";
import api from "../../../services/api";
import toast from "../../../lib/ToastNotification";
import APIGet from "../../../lib/api-get";

export default {
  name: "GroupCategories",

  components: { NewAssociation, ErrorsMessages, ConfirmationDialog },

  data() {
    return {
      errors: [],
      loading: false,

      stock_groups: [],
      stock_item_categories: [],

      search: "",
      headers: [
        { text: "GRUPO", value: "group_name" },
        { text: "CATEGORIA", value: "category_name" },
        { text: "AÇÕES", value: "actions", sortable: false, width: "150px" },
      ],
      raw_associations: [],
    };
  },

  computed: {
    associations() {
      return this.raw_associations.map((item) => ({
        ...item,
        group_name: this.getGroupName(item.stock_group_id),
        category_name: this.getCategoryName(item.stock_item_category_id),
        stock_item_type_name: this.getCategoryTypeName(
          item.stock_item_category_id
        ),
      }));
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    getCategoryIcon(typeName) {
      const name = typeName.toLowerCase();
      if (name.includes("medicamento") || name.includes("medicine"))
        return "fas fa-pills";
      if (name.includes("produto") || name.includes("product"))
        return "fas fa-box";
      return "fas fa-tag";
    },

    getTypeBadgeClass(typeName) {
      const name = typeName.toLowerCase();
      if (name.includes("medicamento") || name.includes("medicine"))
        return "type-medicine";
      if (name.includes("produto") || name.includes("product"))
        return "type-product";
      return "type-default";
    },

    getGroupName(id) {
      const group = this.stock_groups.find((item) => item.id === id);
      return group ? group.name : id;
    },

    getCategoryName(id) {
      const category = this.stock_item_categories.find(
        (item) => item.id === id
      );
      return category ? category.name : id;
    },

    getCategoryTypeName(id) {
      const category = this.stock_item_categories.find(
        (item) => item.id === id
      );
      return category ? category.stock_item_type_name : "";
    },

    async retrieve() {
      await this.getStockGroups();
      await this.getStockItemCategories();
      await this.getAssociations();
    },

    async getAssociations() {
      try {
        this.loading = true;

        const response = await api.get(
          "/rest-home/stock/groups/associated-categories"
        );
        const { error, errors, associations } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.raw_associations = associations;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getStockGroups() {
      this.loading = true;
      const { error, errors, stock_groups } = await APIGet.getStockGroups();
      this.errors = errors;
      this.stock_groups = error ? [] : stock_groups;
      this.loading = false;
    },

    async getStockItemCategories() {
      this.loading = true;
      const { error, errors, stock_item_categories } =
        await APIGet.getStockItemCategories();
      this.errors = errors;
      this.stock_item_categories = error ? [] : stock_item_categories;
      this.loading = false;
    },

    async disassociate(stock_group_id, stock_item_category_id) {
      try {
        this.loading = true;

        const params = { stock_group_id, stock_item_category_id };
        const response = await api.delete(
          "/rest-home/stock/group/disassociate-category",
          { params }
        );
        const { error, errors, message } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          toast(message, "success");
          this.getAssociations().then();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.group-categories-modern {
  background: transparent;
}

/* Content Section */
.categories-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Header Section */
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 24px;
}

.header-left {
  flex: 1;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
}

.section-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 20px;
}

.section-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 400px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-input {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-input:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;
  align-items: start;
}

/* Table Section */
.table-section {
  width: 100%;
}

.no-results {
  background: white;
  border-radius: 16px;
  padding: 60px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

/* Table Cell Styling */
.group-name-cell {
  padding: 8px 0;
}

.group-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.group-icon {
  color: #0f65b0;
  font-size: 12px;
}

.category-name-cell {
  padding: 8px 0;
}

.category-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.category-icon {
  color: #0f65b0;
  font-size: 12px;
}

.category-type {
  margin-top: 4px;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-medicine {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.type-product {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #60a5fa;
}

.type-default {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  border: 1px solid #cbd5e1;
}

.actions-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-action-delete {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3) !important;
  transition: all 0.3s ease !important;
  padding: 6px 12px !important;
}

.btn-action-delete:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4) !important;
}

/* Info Section */
.info-section {
  position: sticky;
  top: 24px;
}

.info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.info-header {
  background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
  padding: 20px;
  border-bottom: 1px solid #0284c7;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0;
  display: flex;
  align-items: center;
}

.info-title i {
  color: #0284c7;
}

.info-content {
  padding: 24px;
}

.info-item {
  margin-bottom: 24px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.info-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

/* Rules List */
.rules-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rule-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.rule-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  flex-shrink: 0;
}

.rule-icon.restriction {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.rule-icon.best-practice {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.rule-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rule-content strong {
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
}

.rule-content span {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

/* Examples List */
.examples-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.example-association {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.association-group,
.association-category {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  color: #374151;
  font-weight: 500;
}

.association-arrow {
  color: #0f65b0;
  font-size: 12px;
}

.example-icon {
  color: #0f65b0;
  font-size: 12px;
  width: 14px;
}

/* Steps List */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.step-number {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-content strong {
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
}

.step-content span {
  font-size: 12px;
  color: #64748b;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .info-section {
    position: static;
  }
}

@media (max-width: 1024px) {
  .content-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .filters-card {
    padding: 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .actions-cell {
    flex-direction: column;
    gap: 4px;
  }

  .btn-action-delete {
    width: 100% !important;
    justify-content: center !important;
  }

  .example-association {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .association-arrow {
    transform: rotate(90deg);
  }

  .info-content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 18px;
    flex-direction: column;
    gap: 4px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .info-header {
    padding: 16px;
  }

  .info-title {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

/* Enhanced hover effects */
.table-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease !important;
}

.info-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease !important;
}
</style>